import { isNOU } from "@ms-ofb/officefloodgatecore/dist/src/Utils";
import { Api } from "@ms-ofb/floodgate-shared-typings";
import { IDs, PromptConstants } from "./../UiConstants";
import * as Configuration from "./../../Configuration/Configuration";
import {
	renderCVSurvey,
	ISurveyPayloadAdditionalInfo, EventType,
	QuestionType, ISurveyQuestionAnswer,
} from "gatecrash";
import { Transporter } from "./../../Transport/Transport";
import * as Logging from "./../../Logging/Logging";
import { customerVoiceSurveyUX } from "../../Constants";
import { IOnDismissDelegate } from "../../IOnDismissDelegate";
import { IContextParams, ISurveyOptionalConfigParams, ISurveyRenderContext } from "../../IRenderSurvey";
import { IHandleSurveyEvent, ISurveyEventData, SurveyEventKey } from "../../IHandleSurveyEvent";
import { IOverrideRenderCallback } from "../../IOverrideRenderCallback";

/**
 * Callback when the CV Survey dialog is dismissed
 */
let onDismiss: IOnDismissDelegate;
let onDismissFlag = false;
let isPromptResponseLogValidated = false;
let isAdditionalPromptResponseLogValidated = false;
let hasSubmittedData = false;
const SurveyUIFormSecondarySubmitName = "Survey_UI_Form_Secondary_Submit_Page_Number_";
const SurveyUIFormUserClosedName = "Survey_UI_Form_UserClosed_Page_Number_";
let firstCommentResponse: string = null;
let firstCommentTitle = "";

/**
 * Function to show Customer Voice survey experience
 */
export function createCVSurvey(
	contextParams: IContextParams,
	onClose: () => void,
	cvSurveyOptionalConfigParams?: ISurveyOptionalConfigParams,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback,
): ISurveyRenderContext {
	onDismiss = function (submitted: boolean) {
		Configuration.get().getFloodgateInitOptions().onDismiss(
			Configuration.get().getFloodgateSurvey().getCampaignId(),
			submitted
		);
		onClose();
	};

	if (overrideUXRenderCallbackAsync) {
		const logCustomProps: Logging.ICustomProperties = {
			CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey().getId(),
			SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
		};

		overrideUXRenderCallbackAsync(contextParams)
			.then(() => {
				internal.logTelemetryEvent(Logging.EventIds.SURVEY_RENDER_OVERRIDE_SUCCESS, logCustomProps);
			}).catch((ex) => {
				logCustomProps.ErrorMessage = ex;
				internal.logTelemetryEvent(Logging.EventIds.SURVEY_RENDER_OVERRIDE_ERROR, logCustomProps);
			});
		return {
			updateStyling: () => { return; },
		};
	} else {
		const renderContext: ISurveyRenderContext = renderCVSurvey(
			IDs.ToastContainer,
			handleSurveyEvent,
			contextParams,
			cvSurveyOptionalConfigParams
		);

		internal.logTelemetryEvent(Logging.EventIds.SURVEY_UI_RENDER_CV_SURVEY_COMPLETED);
		return renderContext;
	}
}

/**
 * The default handler of events emitted by custom survey tools.
 * @param key an identifier for the event
 * @param data all data needed to process the event
 */
export const handleSurveyEvent: IHandleSurveyEvent = (
	key: SurveyEventKey,
	data: ISurveyEventData,
): void => {
	let keyPrefix: string;
	let keyBase: string;
	let err = false;

	const splitKey: string[] = key.split("_");
	if (splitKey.length > 1) {
		keyPrefix = splitKey[0];
		keyBase = splitKey[1];
	} else {
		keyPrefix = "Other";
		keyBase = splitKey[0];
	}

	switch (keyPrefix) {
		case "CV":
			switch (keyBase) {
				case "OnSubmit": return onCVSubmit(data);
				case "OnCancel": return onCVCancel(data);
				case "OnLoaded": return onCVLoaded(data);
				case "OnLog": return logGatecrashEvent(data);
				default: err = true;
			}
			break;
		default: err = true;
	}

	if (err) {
		const logCustomProps: Logging.ICustomProperties = {
			CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey().getId(),
			SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
		};
		internal.logTelemetryEvent(Logging.EventIds.SURVEY_EVENT_KEY_ERROR, logCustomProps);
	}
};

// Setting Rating field for SubmitData
export function getRating(values: ISurveyQuestionAnswer): number {
	return parseInt(values?.answerValue[0], 10) - 1;
}

// Setting QuestionValue field for SubmitData
export function getQuestionValue(values: ISurveyQuestionAnswer): string {
	return values?.questionValue;
}

// Setting AnswerValue field for SubmitData
export function getAnswerValue(values: ISurveyQuestionAnswer): string {
	return isNOU(values?.answerValue[0]) ? values?.answerValue[0] : `${values?.answerValue[0]}`;
}

// Setting InsightfulOptions for insightful field for SubmitData
export function getInsightfulOptions(values: ISurveyQuestionAnswer): string[] { // Evaluate
	return values?.insightfulQuestionOptions;
}

// Setting InsightfulAnswers for insightful field for SubmitData
export function getInsightfulAnswers(values: ISurveyQuestionAnswer): boolean[] { // Evaluate
	return values?.insightfulQuestionAnswers;
}

export interface IFormattedData {
	rating: number;
	commentsCollection?: string[];
	email?: string;
	ratingQuestion?: string;
	ratingsListLength: number;
	promptQuestion?: string;
	promptResponse?: string;
	commentTitlesCollection?: { question: string }[];
	additionalPromptQuestion?: string;
	additionalPromptResponse?: string;
	insightfulQuestionOptionsCollection?: { question: string, options: string[] }[];
	insightfulQuestionAnswersCollection?: boolean[][];
}

/* Traverses the CV Survey response object array and creates props for OCV Payload; 
 * on the face of it though it is O(N^2) runtime complexity, 
 * given the number of pages are only in finite number 
 * (wouldn't expect more than 10 pages at most), this should still run in constant time. 
 */
export function formatSubmitData(submitData: ISurveyEventData): IFormattedData {

	let rating: number;
	const commentsCollection: string[] = [];
	let email: string;
	let ratingQuestion: string;
	let ratingsListLength: number;
	let promptQuestion: string;
	let promptResponse: string;
	const commentTitlesCollection: { question: string }[] = [];
	let additionalPromptQuestion: string;
	let additionalPromptResponse: string;
	const insightfulQuestionOptionsCollection: { question: string, options: string[] }[] = [];
	const insightfulQuestionAnswersCollection: boolean[][] = [];
	let isFirstCommentValidated = false;
	let isFirstEmptyCommentAdded = false;

	submitData.pageInfo?.forEach(currentPage => {

		currentPage.questionAnswers.forEach(values => {

			switch (values?.questionType) {

				case QuestionType.Rating:

					rating = getRating(values);
					ratingQuestion = getQuestionValue(values);
					ratingsListLength = values.ratingsListLength;

					break;
				case QuestionType.Prompt:

					promptQuestion = getQuestionValue(values);
					promptResponse = getAnswerValue(values);

					break;

				case QuestionType.Verbatim: {
					const commentResponse = getAnswerValue(values);
					const commentTitle = getQuestionValue(values);
					const cvSurveyHasPromptPage = !isNOU(promptResponse);
					let setCommentTitleAndValue = false;

					// This is a workaround because the OCV schema presents issues in the comments values section
					// when we send an array of null in the extended manifest data.
					// We will update this logic once the OCV team delivers the fix.
					if (isFirstCommentValidated) {
						if (
							!cvSurveyHasPromptPage &&
							submitData.pageNumber !== "1" &&
							isNOU(firstCommentResponse) &&
							!isFirstEmptyCommentAdded
						) {
							commentTitlesCollection.push({ question: firstCommentTitle });
							commentsCollection.push(" ");
							isFirstEmptyCommentAdded = true;
						}
						setCommentTitleAndValue = cvSurveyHasPromptPage || submitData.pageNumber !== "1";
					} else {
						firstCommentResponse = commentResponse;
						firstCommentTitle = commentTitle;
						isFirstCommentValidated = true;
						setCommentTitleAndValue = cvSurveyHasPromptPage || !isNOU(firstCommentResponse);
					}

					if (setCommentTitleAndValue) {
						commentTitlesCollection.push({ question: commentTitle });
						commentsCollection.push(isNOU(commentResponse) ? " " : commentResponse);
					}

					break;
				}

				case QuestionType.Email:
					email = getAnswerValue(values);

					break;

				case QuestionType.AdditionalQuestion:

					additionalPromptQuestion = getQuestionValue(values);
					additionalPromptResponse = getAnswerValue(values);

					break;

				case QuestionType.ContactMe:
				default:

					insightfulQuestionOptionsCollection.push(
						{
							question: (values?.questionType === QuestionType.ContactMe) ? QuestionType.ContactMe : getQuestionValue(values),
							options: getInsightfulOptions(values),
						}
					);

					insightfulQuestionAnswersCollection.push(getInsightfulAnswers(values));

			}

		});

	});

	const formattedData: IFormattedData = {
		rating,
		ratingQuestion,
		ratingsListLength,
		email,
		commentsCollection,
		commentTitlesCollection,
		promptQuestion,
		promptResponse,
		additionalPromptQuestion,
		additionalPromptResponse,
		insightfulQuestionOptionsCollection,
		insightfulQuestionAnswersCollection,
	};

	return formattedData;
}

function updateExtendedManifestData(transporter: Transporter, formattedData: IFormattedData): string {
	let commentValues: string[];
	let commentTitles: { question: string }[] = [];

	if (formattedData.commentsCollection.length > 0) {
		commentValues = formattedData.commentsCollection;
		commentTitles = formattedData.commentTitlesCollection;
	}

	/* Titles */
	const extendedManifestData = JSON.parse(transporter.getExtendedManifestData()) as any;
	extendedManifestData.surveyStrings = JSON.stringify({
		prompt: { title: "", question: formattedData?.promptQuestion, yesButtonText: "YES", noButtonText: "NO" },
		rating: [{ question: formattedData?.ratingQuestion, options: [] }],
		comment: commentTitles,
		multipleChoice: formattedData.insightfulQuestionOptionsCollection,
	});
	/* Values */
	extendedManifestData.surveySpecificData = JSON.stringify({
		rating: [{ numberOfOptions: formattedData.ratingsListLength, selectedRating: formattedData.rating }],
		comment: commentValues,
		multipleChoice: formattedData.insightfulQuestionAnswersCollection,
	});
	transporter.setExtendedManifestData(JSON.stringify(extendedManifestData));
	return extendedManifestData;
}

function updateAppData(): string {
	const appData = Configuration.get().getCommonInitOptions().applicationGroup.appData;
	const appDataValues: unknown[] = [];

	// To differentiate CV survey responses from other surveys given the flight object in OCV gets trimmed, this is a workaround for now.
	const cvSurveyInfo: ISurveyPayloadAdditionalInfo = {
		additionalInfo: {
			SurveyDescription: customerVoiceSurveyUX,
			Flight: Configuration.get().getCVSurveyFlights(),
			IsCopilotLicenseEnabled: Configuration.get().isCopilotLicenseEnabled().toString()
		},
	};

	if (isNOU(appData)) {
		appDataValues.push({});
	} else {
		try {
			appDataValues.push(JSON.parse(appData));
		} catch (ex) {
			logTelemetryEvent(Logging.EventIds.APPDATA_JSON_PARSE_ERROR);
			appDataValues.push({});
		}
	}

	appDataValues.push(cvSurveyInfo);
	return JSON.stringify(appDataValues);
}

function setTransporterValues(transporter: Transporter, formattedData: IFormattedData): Transporter {
	transporter.setManifestValues(Configuration.get().getFloodgateSurvey().getJsonElements());
	updateExtendedManifestData(transporter, formattedData);

	if (formattedData.email) {
		transporter.setEmail(formattedData.email);
	}
	const appData = updateAppData();
	transporter.setAppData(appData);

	const preGeneratedId = Configuration.get().getFloodgateSurvey().getPreGeneratedClientFeedbackId();
	if (preGeneratedId) {
		transporter.setClientFeedbackId(preGeneratedId);
	}

	return transporter;
}

/**
 * Call-back function when user sends feedback from Customer Voice survey experience.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function onCVSubmit(data: ISurveyEventData): void {
	try {

		const formattedData = formatSubmitData(data);
		const { rating, promptResponse, additionalPromptResponse } = formattedData;

		const isPromptAnswerLogMandatory: boolean = validateLogPromptAnswer(promptResponse, additionalPromptResponse);
		// onCVSubmit is called twice when the user clicks NotNow an ICM was created for the CV team to avoid this behavior
		if (isPromptAnswerLogMandatory) {
			return;
		}

		// clientfeedback id to overwrite
		const transporter: Transporter = new Transporter(
			Configuration.get().getCommonInitOptions().environment,
			Api.SurveyType[Configuration.get().getFloodgateSurvey().getSurveyType()],
			Configuration.get().getCommonInitOptions().appId,
			"Survey",
			Configuration.get().getCommonInitOptions().applicationGroup,
			Configuration.get().getCommonInitOptions().telemetryGroup,
			Configuration.get().getCommonInitOptions().webGroup,
		);

		const comment = isNOU(firstCommentResponse) ? null : firstCommentResponse;
		Configuration.get().getFloodgateSurvey().setValues(Number(rating), comment ? comment : null);
		setTransporterValues(transporter, formattedData);
		transporter.submit();

		if (
			data.eventType === EventType.FormPartialResponseSubmitted ||
			data.eventType === EventType.FormResponseSubmitted
		) {
			// Toggle OnDismissFlag when data is submitted to OCV
			onDismissFlag = true;
			if (hasSubmittedData) {
				Logging.EventIds.SURVEY_UI_FORM_SECONDARY_SUBMIT.name =
					SurveyUIFormSecondarySubmitName + data.pageNumber;
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SECONDARY_SUBMIT);
			} else {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SUBMIT);
				hasSubmittedData = true;
			}
		}

	} catch (ex) {
		logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SUBMIT_ERROR);
	}
}

function validateLogPromptAnswer(promptResponse: string, additionalPromptResponse: string): boolean {
	let isPromptAnswerLogNeeded = false;
	let isPromptAnswerNo = false;
	if (!isNOU(promptResponse)) {
		isPromptAnswerLogNeeded = LogPromptAnswer(promptResponse, QuestionType.Prompt, isPromptResponseLogValidated);
		isPromptAnswerNo = promptResponse === PromptConstants.No;
	}

	if (!isNOU(additionalPromptResponse)) {
		isPromptAnswerLogNeeded = LogPromptAnswer(additionalPromptResponse,
			QuestionType.AdditionalQuestion, isAdditionalPromptResponseLogValidated);
		isPromptAnswerNo = additionalPromptResponse === PromptConstants.No;
	}

	return isPromptAnswerLogNeeded || isPromptAnswerNo;
}

function LogPromptAnswer(promptResponse: string, questionType: string, isPromptAnswerLogged: boolean): boolean {

	if (!isPromptAnswerLogged) {

		let promptMessageClicked;
		let promptMessageClosed;
		if (questionType === QuestionType.Prompt) {
			isPromptResponseLogValidated = true;
			promptMessageClicked = Logging.EventIds.SURVEY_UI_PROMPT_CLICKED;
			promptMessageClosed = Logging.EventIds.SURVEY_UI_PROMPT_USERCLOSED;
			if ((promptResponse === PromptConstants.Yes)) {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SHOWN);
			}
		} else {
			isAdditionalPromptResponseLogValidated = true;
			promptMessageClicked = Logging.EventIds.SURVEY_UI_ADDITIONALPROMPT_CLICKED;
			promptMessageClosed = Logging.EventIds.SURVEY_UI_ADDITIONALPROMPT_USERCLOSED;
		}

		logTelemetryEvent(promptResponse === PromptConstants.No ? promptMessageClosed : promptMessageClicked);

		return true;
	}
	return false;
}

/**
 * Call-back function when user cancels feedback from Customer Voice survey experience.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function onCVCancel(data: ISurveyEventData): void {
	switch (data.eventType) {
		case EventType.FormClosed:
			if (isNOU(data.pageNumber)) {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			} else {
				Logging.EventIds.SURVEY_UI_FORM_USERCLOSED.name = SurveyUIFormUserClosedName + data.pageNumber.charAt(data.pageNumber.length - 1);
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			}
			break;
		case EventType.ThankYouPageClosed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_THANKYOUPAGE_USERCLOSED);
			break;
		case EventType.PromptClosed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_PROMPT_USERCLOSED);
			break;
		case EventType.AdditionalPromptClosed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_ADDITIONALPROMPT_USERCLOSED);
			break;
		case EventType.FormAutoDismissed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_PROMPT_AUTODISMISSED);
			break;
		case EventType.ThankYouPageAutoDismissed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_THANKYOUPAGE_AUTODISMISSED);
			break;
		case EventType.FormResponseCanceled:
			if (isNOU(data.pageNumber)) {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			} else {
				Logging.EventIds.SURVEY_UI_FORM_USERCLOSED.name = SurveyUIFormUserClosedName + data.pageNumber?.charAt(data.pageNumber.length - 1);
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			}
			break;
		default:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_NO_RETURN_TYPE);
			break;
	}

	onDismiss(onDismissFlag);
}

/**
 * Call-back function when user Customer Voice survey is loaded and ready for showing to the user.
 * Here we are going to hide the survey if the current survey is the dispatcher survey.
 * First if the dispacther survey is called, the hasSurveyDispatcher parameter will be true.
 * And second, dispatcher survey will be the first survey in the survey dispatcher logic. So, we using 
 * hasLoadedDispatcher to make sure we will only hide the first survey in the whole survey dispatcher logic
 * which is the dispatcher survey.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function onCVLoaded(data: ISurveyEventData): void {
	data.eventId = Logging.EventIds.SURVEY_UI_FORM_SHOWN;

	if (data.hasSurveyDispatcher) {
		data.eventId = Logging.EventIds.SURVEY_UI_FORM_DISPATCHER_IMPLEMENTED;
	} else if (data.hasSurveyPromptShown) {
		data.eventId = Logging.EventIds.SURVEY_UI_PROMPT_SHOWN;
	}

	logGatecrashEvent(data);
}

/**
 * Call-back logging function for gatecrash errors and gatecrash events.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function logGatecrashEvent(data: ISurveyEventData): void {
	const configuration = Configuration.get();
	const logCustomProps: Logging.ICustomProperties = {
		TimeMilliseconds: data.TimeMilliseconds,
		CampaignId: configuration.getFloodgateSurvey().getCampaignId(),
		SurveyId: configuration.getFloodgateSurvey().getId(),
		SurveyType: configuration.getFloodgateSurvey().getSurveyType(),
		ErrorMessage: data.customProperties?.ErrorMessage ? data.customProperties?.ErrorMessage : null,
		Message: data.customProperties?.Message ? data.customProperties?.Message : null,
	};

	logTelemetryEvent(data.eventId, logCustomProps);
}

// Log Telemetry for Customer Voice survey funnel.
function logTelemetryEvent(eventId: Logging.IEventId, customProperties?: Logging.ICustomProperties): void {
	const configuration = Configuration.get();
	let logCustomProps: Logging.ICustomProperties;
	if (!customProperties) {
		logCustomProps = {
			CampaignId: configuration.getFloodgateSurvey().getCampaignId(),
			SurveyId: configuration.getFloodgateSurvey().getId(),
			SurveyType: configuration.getFloodgateSurvey().getSurveyType(),
			ClientFeedbackId: Configuration.get().getFloodgateSurvey().getPreGeneratedClientFeedbackId(),
			CVFormId: configuration.getFloodgateSurvey().getCVSurveyFormId(),
		};
	} else {
		logCustomProps = customProperties;
	}

	Logging.getLogger().logEvent(
		eventId,
		Logging.LogLevel.Critical,
		logCustomProps
	);
}

/**
 * Take a dictionary of CSS variables or HTML data attributes and pass them to the CV iframe for injection.
 * This allows dynamic CSS changes within CV survey at runtime.
 * 
 * NOTE: All "runtime" CSS variable keys must be prefixed with "--rt", and HTML data attributes must be prefixed
 * with "data-rt", otherwise CV will reject them.
 *
 * Example - CSS Variables
 * -----------------------
 *    Input dict:
 *        { "--rt-background-color": "black" }
 *
 *    Result (in CV CSS file):
 *        :root {
 *          --rt-background-color: black;
 *        }
 *
 *    Usage (in CV CSS file):
 *        .some-css-selector {
 *          background-color: var(--rt-background-color);
 *        }
 * 
 * Example - HTML Data Attributes
 * ------------------------------
 *    Input dict:
 *        { "data-rt-theme-color": "dark" }
 *
 *    Result (in CV HTML):
 *        <html data-rt-theme-color="dark"> ... </html>
 *
 *    Usage (in CV CSS file):
 *        [data-rt-theme-color="dark"].some-css-selector {
 *          background-color: black;
 *          color: white;
 *          border; 2px solid white;
 *        }
 *
 * @param {{ [key: string]: string }} cssVars       - Dictionary of HTML data attributes (key: val) to be injected
 *                                                    Leave empty ({}) if only injecting HTML data attributes
 * @param {{ [key: string]: string }} htmlDataAttrs - Dictionary of HTML data attributes (key: val) to be injected
 *                                                    Leave empty ({}) if only injecting CSS vars
 */
export function updateSurveyStyling(cssVars: { [key: string]: string }, htmlDataAttrs: { [key: string]: string }): void {
	const renderContext: ISurveyRenderContext | null = Configuration.get().getCVRenderContext();
	if (renderContext) {
		renderContext.updateStyling(cssVars, htmlDataAttrs);
	} else {
		const logCustomProps: Logging.ICustomProperties = {
			CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey().getId(),
			SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
		};

		internal.logTelemetryEvent(Logging.EventIds.SURVEY_RENDER_CONTEXT_ERROR, logCustomProps);
	}
}

const internal = {
	logTelemetryEvent,
};

export const { internal: unitTestExports } = { internal };
